import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import i18next from 'i18next';
import _ from 'lodash';
import { Dropdown } from 'primereact/dropdown';
import axiosInstance from '../../../config/axiosConfig';
import useSelectedCompanyStore from '../../../store/useSelectedCompanyStore';
import clsx from 'clsx';
import ComparisonCandidateAttemptRadarChart from './ComparisonCandidateAttemptRadarChart';
import { ProgressSpinner } from 'primereact/progressspinner';

const t = (i18nKey, params) => i18next.t(`userDashboard.compare.${i18nKey}`, params);

const useCountAnimation = (targetValue, duration = 800) => {
    const [count, setCount] = useState(0);
    const startTimeRef = useRef(null);
    const startValueRef = useRef(0);
    const frameRef = useRef();

    useEffect(() => {
        startValueRef.current = count;
        startTimeRef.current = null;
        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const progress = (timestamp - startTimeRef.current) / duration;
            if (progress < 1) {
                setCount(startValueRef.current + (targetValue - startValueRef.current) * progress);
                frameRef.current = requestAnimationFrame(animate);
            } else {
                setCount(targetValue);
            }
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
            }
        };
    }, [targetValue, duration]);

    return Math.round(count);
};

function CandidateBox({ candidate, campaignName, potential, candidate_number, campaign_id, candidate_id, attempt_ref, activeTab, isLoading }) {
    const participantFirstName = candidate ? `${_.capitalize(candidate.first_name)}` : t('defaultParticipant');
    const participantLastName = candidate ? `${_.capitalize(candidate.last_name)}` : '';
    const isGrayedOut = !candidate;
    const history = useHistory();

    return (
        <div className={clsx('box candidate-box', { 'is-primary': candidate_number === 1 })}>
            <div className='p-1 my-2'>
                <h3 className={clsx('title is-4 has-text-centered is-capitalized text-is-ellipsis mb-0', { 'has-text-dgrad-deepsy': candidate_number === 1, 'has-text-dgrad-blue': candidate_number === 2 })}>
                    {participantFirstName}
                </h3>
                <h3 className={clsx('title is-4 has-text-centered is-capitalized text-is-ellipsis mt-0', { 'has-text-dgrad-deepsy': candidate_number === 1, 'has-text-dgrad-blue': candidate_number === 2 })}>
                    {participantLastName}
                </h3>
            </div>

            <p className="subtitle is-6 has-text-centered has-text-grey">
                {campaignName || 'No campaign name somehow'}
            </p>

            {potential && Object.keys(potential).length > 0 && (
                <div className="mt-4">
                    {Object.entries(potential).sort((a, b) => b[0].length - a[0].length).map(([key, value]) => (
                        <div className="column is-fullwidth potential-column p-0" key={key}>
                            <button className={clsx('d-button is-outlined potential-button', { 'green-deepsy': candidate_number === 1 })} style={{ transition: 'box-shadow 0.3s ease' }}>
                                <span className="potential-label">{key}</span>

                                <div className={clsx('potential-value')}>
                                    <span className={clsx({ 'has-text-dgrad-deepsy': candidate_number === 1, 'has-text-dgrad-blue': candidate_number === 2 })}>
                                        {value}%
                                    </span>
                                </div>

                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className={clsx('has-text-centered', 'mt-4 bottom-button', {
                'is-invisible': candidate_number === 2 && activeTab === 'jobs'
            })}>
                <a className={clsx('d-button', {
                    'green-deepsy': candidate_number === 1,
                    'is-disabled': isGrayedOut || isLoading
                })}
                    style={{ fontFamily: '"Inter", sans-serif' }}
                    onClick={() => {
                        if (!isGrayedOut && !isLoading && campaign_id && candidate_id && attempt_ref) {
                            history.push(`/dashboard/campaigns/${campaign_id}/candidates/${candidate_id}/${attempt_ref}`);
                        }
                    }}
                >{t('detailedResults')}</a>
            </div>
        </div>
    );
}

function SubScoreComparisonRow({ scoreName, candidate1Score, candidate2Score, current_test, candidate1AttemptScore, candidate2AttemptScore, isFirst, isJobComparison, testColor }) {
    const leftWidth = 50 * (candidate1Score / 100);
    const rightWidth = 50 * (candidate2Score / 100);
    const animatedScore1 = useCountAnimation(isFirst && !isJobComparison ? candidate1AttemptScore : candidate1Score);
    const animatedScore2 = useCountAnimation(isFirst && !isJobComparison ? candidate2AttemptScore : candidate2Score);

    return (
        <div className={clsx('score-comparison-row', {
            'mb-6': isFirst && !isJobComparison,
            'px-5': !isFirst && !isJobComparison
        })}>
            <div className="score-labels">
                <span className={clsx('has-text-dgrad-deepsy', {
                    'is-size-4': !isFirst || isJobComparison,
                    'is-size-3': isFirst && !isJobComparison
                })}>
                    {animatedScore1}%
                </span>

                {/* TODO: Capitalize, not with css since there can be more than one word, so use js */}
                <span className="score-name is-size-5 has-text-grey">
                    {isFirst && !isJobComparison ? current_test.sub_title : scoreName}
                </span>

                <span className={clsx('has-text-dgrad-blue', {
                    'is-size-4': !isFirst || isJobComparison,
                    'is-size-3': isFirst && !isJobComparison
                })}>
                    {animatedScore2}%
                </span>
            </div>

            <div className={clsx('comparaison-progressbar', { 'is-large': isFirst && !isJobComparison })}>
                <div
                    className="progress-fill-left"
                    style={{
                        width: `${leftWidth}%`,
                        backgroundColor: isJobComparison ? testColor : undefined
                    }}
                />
                <div
                    className="progress-fill-right"
                    style={{
                        width: `${rightWidth}%`,
                        backgroundColor: isJobComparison ? testColor : undefined
                    }}
                />
            </div>
        </div>
    );
}

const Comparison = () => {
    const REQUIRED_JOB_TEST_KEYS = [
        'ex-vert-v-3-fr',
        'hop-n',
        'nayce-1',
        'testconsc',
        'harmonia'
    ];

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);
    const [filterCurrentCampaign, setFilterCurrentCampaign] = useState(true);
    const [candidates, setCandidates] = useState([]);
    const [queriedCandidates, setQueriedCandidates] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [comparisonData, setComparisonData] = useState(null);
    const [nextTestLoading, setNextTestLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('candidates');
    const [jobsList, setJobsList] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);
    const [jobComparisonData, setJobComparisonData] = useState(null);
    const [jobSearchQuery, setJobSearchQuery] = useState('');
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [jobFilterValue, setJobFilterValue] = useState('');

    const { campaign_id } = useParams();

    const fetchCandidates = async (query = '', limit = 100) => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get(`/dashboard/candidates/${selectedCompany.company_id}/compareList`, { params: { page: 1, limit, query, filter_tests: filterCurrentCampaign, campaign_id } });
            setQueriedCandidates(response.data.items);
        } catch (error) {
            console.error('Error fetching candidates:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCandidates();
    }, [filterCurrentCampaign]);

    useEffect(() => {
        if (jobsList.length > 0) {
            const filtered = jobsList.filter(job => {
                const jobName = (job.Job || '').toLowerCase();
                const searchTerm = jobSearchQuery.toLowerCase();
                return jobName.includes(searchTerm)
            });
            setFilteredJobs(filtered);
        }
    }, [jobSearchQuery, jobsList]);

    const handleCandidateChange = async (index, candidateId) => {
        const updatedCandidates = [...candidates];
        updatedCandidates[index] = candidateId;
        if (index === 0 && !candidateId) {
            updatedCandidates[1] = null;
        }
        setCandidates(updatedCandidates);
        if (updatedCandidates.filter(Boolean).length > 0) {
            await handleCompare(updatedCandidates);
        } else {
            setComparisonData(null);
        }
    };

    const itemTemplate = (candidate) => {
        if (!candidate) return t('selectParticipant');
        return (
            <div className="autocomplete-suggestion mb-1">
                <strong>
                    {`${candidate?.first_name || ''} ${candidate?.last_name || ''}`}
                </strong>
                <div className="autocomplete-suggestion-adress pb-0">
                    <span>{candidate?.email || 'Aucun email associé'}</span>
                </div>
                <div className="autocomplete-suggestion-adress pb-0">
                    <span>{`Tests réalisés: ${candidate?.choosen_tests?.length || 0}`}</span>
                </div>
            </div>
        );
    };

    const jobItemTemplate = (job) => {
        if (!job) return t('selectJob');
        return (
            <div className="py-2">
                {job.Job}
            </div>
        );
    };

    const handleNextTest = async () => {
        if (nextTestLoading) return;
        try {
            setNextTestLoading(true);

            const nextTestRef = comparisonData[0]?.next_test?.ref;
            if (!nextTestRef) return;

            const selectedCandidatesData = candidates.filter(Boolean).map((candidateId) => queriedCandidates.find((c) => c.candidate_id === candidateId));

            const formattedCandidates = selectedCandidatesData.map((candidate) => ({ candidate_id: candidate.candidate_id }));

            const response = await axiosInstance.post(`/dashboard/candidates/${selectedCompany?.company_id}/compareCandidates`, { test_ref: nextTestRef, candidates: formattedCandidates });
            setComparisonData(response.data);
        } catch (error) {
            console.error('Error fetching next test comparison:', error);
        } finally {
            setNextTestLoading(false);
        }
    };

    const handleSearchChange = (query) => {
        setSearchQuery(query);
        fetchCandidates(query);
    };

    const handleCompare = async (updatedCandidates) => {
        try {
            setIsLoading(true);
            const selectedCandidatesData = updatedCandidates.filter(Boolean).map((candidateId) =>
                queriedCandidates.find((c) => c.candidate_id === candidateId)
            );
            const firstCandidateTests = selectedCandidatesData[0]?.choosen_tests?.[0] || {};

            const testRef = Object.keys(firstCandidateTests)[0] || '';

            const formattedCandidates = selectedCandidatesData.map((candidate) => ({
                candidate_id: candidate.candidate_id
            }));
            const response = await axiosInstance.post(`/dashboard/candidates/${selectedCompany?.company_id}/compareCandidates`, { test_ref: testRef, candidates: formattedCandidates });
            setComparisonData(response.data);
        } catch (error) {
            console.error('Error comparing candidates:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredCandidates = candidates[0]
        ? queriedCandidates.filter((candidate) => {
            if (candidate.candidate_id === candidates[0]) return false;

            const currentTestRef = comparisonData?.[0]?.current_test?.ref;
            if (!currentTestRef) return false;

            return candidate.choosen_tests?.some((test) => Object.keys(test).includes(currentTestRef));
        })
        : queriedCandidates;

    const fetchJobsList = async () => {
        try {
            const response = await axiosInstance.get(`/files/dashboard/jobs_list_${window.locale || 'fr'}.json`);
            setJobsList(response.data);
        } catch (error) {
            console.error('Error fetching jobs:', error);
        }
    };

    const hasRequiredJobTests = (candidate) => {
        if (!candidate.choosen_tests || !Array.isArray(candidate.choosen_tests)) {
            return false;
        }
        return candidate.choosen_tests.some(test =>
            Object.keys(test).some(key => REQUIRED_JOB_TEST_KEYS.includes(key))
        );
    };

    const filteredCandidatesForJobs = useMemo(() => {
        return queriedCandidates.filter(hasRequiredJobTests);
    }, [queriedCandidates]);

    const handleJobsTabClick = () => {
        setActiveTab('jobs');
        setCandidates([candidates[0], null]);
        setSelectedJob(null);
        setJobComparisonData(null);
    };

    const handleTabCandidatesClick = () => {
        setActiveTab('candidates');
    };

    useEffect(() => {
        fetchJobsList();
    }, []);

    return (
        <div className="container">
            <h2 className="title">{t('title')}</h2>

            <div className="tabs is-boxed is-centered">
                <ul>
                    <li className={clsx({ 'is-active': activeTab === 'candidates' })}>
                        <a onClick={handleTabCandidatesClick}>{t('tabs.participants')}</a>
                    </li>
                    <li className={clsx({ 'is-active': activeTab === 'jobs' })}>
                        <a onClick={handleJobsTabClick}>{t('tabs.jobs')}</a>
                    </li>
                </ul>
            </div>

            <div className="field mb-4 p-3 ml-3">
                <div className="control has-icons-left">
                    <div className="select">
                        <select
                            value={filterCurrentCampaign ? 'current' : 'all'}
                            onChange={(e) => {
                                const isCurrent = e.target.value === 'current';
                                setFilterCurrentCampaign(isCurrent);
                            }}
                        >
                            <option value="current">{t('filterCurrent')}</option>
                            <option value="all">{t('filterAll')}</option>
                        </select>
                    </div>
                    <span className="icon is-left">
                        <i className="fas fa-filter"></i>
                    </span>
                </div>
            </div>

            <div className="candidate-comparison-section position-relative">
                {isLoading && !nextTestLoading && (
                    <div className="comparison-loading-overlay">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    </div>
                )}

                <div className="columns">
                    <div className="column is-3 candidate-box-wrapper">
                        <Dropdown
                            disabled={isLoading || nextTestLoading}
                            className="is-primary"
                            value={candidates[0]}
                            options={activeTab === 'jobs' ? filteredCandidatesForJobs : queriedCandidates}
                            optionValue="candidate_id"
                            optionLabel="first_name"
                            onChange={(e) => handleCandidateChange(0, e.value)}
                            filter
                            filterInputAutoFocus
                            resetFilterOnHide={!!searchQuery}
                            showClear
                            onFilter={(e) => handleSearchChange(e.filter)}
                            filterBy="username,first_name,last_name,email"
                            itemTemplate={itemTemplate}
                            placeholder={t('participant', { number: 1 })}
                        />
                        <CandidateBox
                            candidate={queriedCandidates.find((c) => c.candidate_id === candidates[0])}
                            campaignName={comparisonData && comparisonData[0]?.campaign_details?.campaign_name ? comparisonData[0]?.campaign_details?.campaign_name : t('noCampaignName')}
                            potential={activeTab !== 'jobs' ? comparisonData && comparisonData[0]?.attempt_data?.potential ? comparisonData[0]?.attempt_data?.potential : {} : {}}
                            candidate_number={1}
                            campaign_id={comparisonData && comparisonData[0]?.campaign_details?.campaign_id ? comparisonData[0]?.campaign_details?.campaign_id : null}
                            candidate_id={comparisonData && comparisonData[0]?.candidate?.candidate_id ? comparisonData[0]?.candidate?.candidate_id : null}
                            attempt_ref={comparisonData && comparisonData[0]?.current_attempt?.attempt_ref ? comparisonData[0]?.current_attempt?.attempt_ref : null}
                            activeTab={activeTab}
                            isLoading={isLoading || nextTestLoading}
                        />
                    </div>
                    <div className="column is-6 sub-scores-container px-1">
                        {activeTab === 'candidates' && comparisonData && comparisonData.length > 0 && (
                            <>
                                <div
                                    className={clsx('mt-2 is-flex is-justify-content-space-between is-align-items-center', {
                                        'is-invisible':
                                            !comparisonData ||
                                            comparisonData.length < 2 ||
                                            !comparisonData[0]?.next_test?.ref ||
                                            !comparisonData[1]?.next_test?.ref ||
                                            comparisonData[0].next_test.ref !==
                                            comparisonData[1].next_test.ref
                                    })}
                                >
                                    <div style={{ width: '5rem' }}></div>
                                    <span style={{
                                        fontSize: '1.8rem',
                                        fontWeight: 700,
                                        color: comparisonData[0]?.current_test?.test_color || '#5684c1'
                                    }}>
                                        {comparisonData[0]?.current_test?.title || ''}
                                    </span>
                                    <i
                                        key={comparisonData[0]?.current_test?.ref || new Date().getTime()}
                                        onClick={handleNextTest}
                                        className={clsx('color-transition', { 'is-clickable': !nextTestLoading, 'fas fa-arrow-right': !nextTestLoading, 'fa-solid fa-circle-notch fa-spin': nextTestLoading })}
                                        style={{
                                            fontSize: '5rem',
                                            background: `linear-gradient(to right, ${comparisonData[0]?.current_test?.test_color || '#5684c1'}, ${comparisonData[0]?.next_test?.test_color || '#5684c1'})`,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            width: 80,
                                            height: 80
                                        }}
                                    />
                                </div>

                                {comparisonData[0]?.attempt_data?.sub_scores.map((scoreName, i) => (
                                    <SubScoreComparisonRow
                                        key={i}
                                        scoreName={scoreName}
                                        candidate1Score={comparisonData[0]?.attempt_data?.sub_scores_scores[i] || 0}
                                        candidate2Score={comparisonData[1]?.attempt_data?.sub_scores_scores[i] || 0}
                                        current_test={comparisonData[0]?.current_test}
                                        candidate1AttemptScore={comparisonData[0]?.current_attempt?.score || 0}
                                        candidate2AttemptScore={comparisonData[1]?.current_attempt?.score || 0}
                                        isFirst={i === 0}
                                    />
                                ))}

                                <div className={clsx({ 'is-hidden': comparisonData[0]?.current_test?.category === 'health' })} style={{ maxWidth: 550, minHeight: 300, margin: '0 auto' }}>
                                    <ComparisonCandidateAttemptRadarChart
                                        enduser_title={comparisonData[0]?.current_test?.sub_title}
                                        enduser_score1={comparisonData[0]?.current_attempt?.score}
                                        enduser_score2={comparisonData[1]?.current_attempt?.score}
                                        top_four_scores1={comparisonData[0]?.attempt_data?.top_four_scores}
                                        top_four_scores2={comparisonData[1]?.attempt_data?.top_four_scores}
                                    />
                                </div>
                            </>
                        )}

                        {activeTab === 'jobs' && jobComparisonData && (
                            <>
                                <div className="has-text-right mt-2">
                                    <i
                                        className="fas fa-arrow-right is-invisible"
                                        style={{
                                            fontSize: '5rem'
                                        }}
                                    />
                                </div>

                                {Object.entries(jobComparisonData.test_results).map(([testKey, testVal]) => {
                                    const jobData = jobsList.find((j) => j.Code === selectedJob);
                                    const candidate1Score = testVal.score || 0;
                                    const candidate2Score = jobData ? jobData[testKey] || 0 : 0;

                                    return (
                                        <SubScoreComparisonRow
                                            key={testKey}
                                            scoreName={testVal.test_info?.sub_title || testKey}
                                            candidate1Score={candidate1Score}
                                            candidate2Score={candidate2Score}
                                            isJobComparison={true}
                                            testColor={testVal.test_info?.test_color}
                                        />
                                    );
                                })}
                            </>
                        )}

                    </div>

                    <div className="column is-3 candidate-box-wrapper">
                        {activeTab === 'candidates' ? (
                            <>
                                <Dropdown
                                    className="is-secondary"
                                    value={candidates[1]}
                                    options={filteredCandidates}
                                    optionValue="candidate_id"
                                    optionLabel="first_name"
                                    onChange={(e) => handleCandidateChange(1, e.value)}
                                    onFilter={(e) => handleSearchChange(e.filter)}
                                    filterBy="username,first_name,last_name,email"
                                    filter
                                    filterInputAutoFocus
                                    resetFilterOnHide={!!searchQuery}
                                    showClear
                                    itemTemplate={itemTemplate}
                                    placeholder={t('participant', { number: 2 })}
                                    disabled={isLoading || nextTestLoading || !comparisonData}
                                />
                                <CandidateBox
                                    candidate={queriedCandidates.find((c) => c.candidate_id === candidates[1])}
                                    campaignName={comparisonData && comparisonData[1]?.campaign_details?.campaign_name ? comparisonData[1]?.campaign_details?.campaign_name : t('noCampaignName')}
                                    potential={comparisonData && comparisonData[1]?.attempt_data?.potential ? comparisonData[1]?.attempt_data?.potential : {}}
                                    candidate_number={2}
                                    campaign_id={comparisonData && comparisonData[1]?.campaign_details?.campaign_id ? comparisonData[1]?.campaign_details?.campaign_id : null}
                                    candidate_id={comparisonData && comparisonData[1]?.candidate?.candidate_id ? comparisonData[1]?.candidate?.candidate_id : null}
                                    attempt_ref={comparisonData && comparisonData[1]?.current_attempt?.attempt_ref ? comparisonData[1]?.current_attempt?.attempt_ref : null}
                                    activeTab={activeTab}
                                    isLoading={isLoading || nextTestLoading}
                                />
                            </>
                        ) : (
                            <>
                                <Dropdown
                                    className='is-secondary'
                                    value={selectedJob}
                                    options={filteredJobs}
                                    optionValue="Code"
                                    optionLabel="Job"
                                    placeholder={t('selectJob')}
                                    filter
                                    filterInputAutoFocus
                                    showClear
                                    filterMatchMode="contains"
                                    filterValue={jobFilterValue}

                                    onChange={(e) => {
                                        setSelectedJob(e.value);
                                        const candidateObj = queriedCandidates.find(
                                            (cand) => cand.candidate_id === candidates[0]
                                        );
                                        if (candidateObj) {
                                            axiosInstance.post(`/dashboard/candidates/${selectedCompany?.company_id}/compareJobs`, { candidate_id: candidateObj.candidate_id }).then((res) => {
                                                setJobComparisonData(res.data);
                                            }).catch((err) => {
                                                console.error('Error fetching job comparison:', err);
                                            });
                                        }
                                    }}
                                    itemTemplate={jobItemTemplate}
                                    disabled={!comparisonData}
                                />
                                <CandidateBox
                                    candidate={queriedCandidates.find((cand) => cand.candidate_id === candidates[0])}
                                    campaignName={jobsList.find((j) => j.Code === selectedJob)?.Job || 'Job'}
                                    potential={{}}
                                    candidate_number={2}
                                    activeTab={activeTab}
                                    isLoading={isLoading || nextTestLoading}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .candidate-comparison-section {
                    position: relative;
                    min-height: 400px;
                }
                
                .comparison-loading-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255, 255, 255, 0.7);
                    z-index: 100;
                }
            `}</style>

            <br className='is-invisible' />
            <br className='is-invisible' />
            <br className='is-invisible' />
            <br className='is-invisible' />
            <br className='is-invisible' />

        </div>
    );
};

export default Comparison;